import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ArrowRightLeft, Download, ExternalLink, Plus, RefreshCw, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import {
  createOrder,
  errorMessage,
  getOrder,
  importOrdersBatch,
  ensureManualPlan,
  getPlan,
  listVehicles,
  type PlanView,
  type Vehicle,
  syncStoreBatch,
  listImports,
  listOrders,
  patchOrder,
  requoteAll,
  uploadFile,
  type CarrierOption,
  type ImportResult,
  type StoreSyncResult,
  type Order,
  type OrderImport,
  type OrderLine,
} from "@/lib/api"
import { day10, dayShort, dm, eur, kg, m3, n, todayLisbon } from "@/lib/format"
import { AsyncButton, ErrorBox, Field, FileButton, inputCls, Loading, Note, Page, Panel, SearchSelect, Tag, Td, Th } from "@/components/kit"
import { Button } from "@/components/ui/button"
import { AssignDialog } from "@/pages/AssignDialog"
import { MoveDialog } from "@/pages/MoveDialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

const OPEN = new Set(["ready", "blocked", "own_fleet", "carrier", "failed"])
export const isOpen = (o: Order) => OPEN.has(o.status)

export const BLOCK_LABEL: Record<string, string> = {
  geo: "sem localização (CP inválido ou não encontrado)",
  weight: "sem peso de algum artigo",
  nosolution: "nenhuma transportadora cumpre o prazo e fica fora da área da frota",
}

type Filter = "all" | "today" | "late" | "carrier" | "fleet" | "blocked" | "weight" | "window"
const FILTERS: Array<[Filter, string]> = [
  ["all", "Todas"],
  ["today", "Vencem hoje"],
  ["late", "Em atraso"],
  ["fleet", "Na área da frota"],
  ["carrier", "Só transportadora"],
  ["blocked", "Por resolver"],
  ["weight", "Peso assumido"],
  ["window", "Janela por confirmar"],
]

function match(o: Order, f: Filter, today: string): boolean {
  const due = day10(o.due_date)
  switch (f) {
    case "all":
      return true
    case "today":
      return isOpen(o) && due === today
    case "late":
      return isOpen(o) && !!due && due < today
    case "fleet":
      return isOpen(o) && o.status !== "blocked" && o.delivery_kind !== "carrier"
    case "carrier":
      return isOpen(o) && o.delivery_kind === "carrier"
    case "blocked":
      return o.status === "blocked"
    case "weight":
      return isOpen(o) && Boolean(o.weight_assumed)
    case "window":
      return o.time_window_source === "ia" && !o.time_window_confirmed
  }
}

export function GeoTag({ o }: { o: Order }) {
  switch (o.geo_precision) {
    case "cp7":
      return <span className="text-xs text-muted-foreground">CP7</span>
    case "cp4":
      return <Tag tone="warn">CP4</Tag>
    case "address":
      return <Tag tone="warn">morada</Tag>
    case "manual":
      return <Tag tone="info">pino manual</Tag>
    default:
      return <Tag tone="bad">{o.postal_code ? "não localizada" : "sem CP"}</Tag>
  }
}

export function LoadCell({ o }: { o: Order }) {
  if (o.block_reason === "weight") return <Tag tone="bad">peso desconhecido</Tag>
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="font-mono text-xs">
        {o.load_class ?? "?"} · {kg(o.total_kg)} · {m3(o.total_m3)}
      </span>
      {o.needs_tail_lift && <Tag tone="info">plataforma</Tag>}
      {o.weight_assumed && <Tag tone="warn">peso assumido</Tag>}
    </div>
  )
}

export function DeadlineCell({ o, today }: { o: Order; today: string }) {
  const due = day10(o.due_date)
  const dl = day10(o.carrier_deadline)
  return (
    <div className="font-mono text-xs">
      <span className={cn(due && due < today && isOpen(o) && "text-amber-700")}>{dm(due)}</span>
      {isOpen(o) &&
        (dl ? (
          <div className={cn("text-[11px]", dl <= today ? "text-amber-700" : "text-muted-foreground")} title="Último dia em que uma transportadora ainda chega no prazo">
            transp. até {dm(dl)}
          </div>
        ) : (
          <div className="text-[11px] text-amber-700">sem transp. de reserva</div>
        ))}
    </div>
  )
}

/** Onde a encomenda está no plano atual (quadro da frota ou transportadora). */
export type Assignment = { kind: "fleet"; label: string; confirmed: boolean } | { kind: "carrier" } | null

export function assignmentsOf(plan: PlanView | null, vehicles: Vehicle[]): Map<string, Assignment> {
  const out = new Map<string, Assignment>()
  if (!plan?.plan) return out
  const routes = new Map(plan.routes.map((r) => [r.id, r]))
  for (const s of plan.stops) {
    if (s.result === "failed") continue
    const r = routes.get(s.route_id)
    if (!r) continue
    out.set(s.order_id, { kind: "fleet", label: `${dayShort(day10(r.day))} · ${vehicles.find((v) => v.id === r.vehicle_id)?.name ?? "veículo"}`, confirmed: r.status === "confirmed" })
  }
  for (const id of plan.plan.carrier) if (!out.has(id)) out.set(id, { kind: "carrier" })
  return out
}

function ChosenCell({ o, at, planned }: { o: Order; at?: Assignment; planned?: boolean }) {
  if (at?.kind === "fleet") {
    return (
      <div>
        <Tag tone={at.confirmed ? "ok" : "info"}>Frota · {at.label}</Tag>
        <div className="mt-1 text-[11.5px] text-muted-foreground">{at.confirmed ? "dia confirmado" : "rascunho"}</div>
      </div>
    )
  }
  if (at?.kind === "carrier") {
    const c = o.options.find((x) => x.service_id === o.carrier_service_id) ?? null
    return (
      <div>
        <Tag tone="neutral">{c?.carrier_name ?? "Transportadora"}</Tag>
        {o.carrier_manual && <span className="ml-1"><Tag tone="info">à mão</Tag></span>}
        <div className="mt-1 font-mono text-[11.5px] text-muted-foreground">{c ? `${eur(c.cost)} · chega ${dayShort(c.arrival)}` : ""}</div>
      </div>
    )
  }
  if (o.status === "blocked") {
    return (
      <div>
        <Tag tone="bad">Por resolver</Tag>
        <div className="mt-1 text-[11.5px] text-muted-foreground">{BLOCK_LABEL[o.block_reason ?? ""] ?? ""}</div>
      </div>
    )
  }
  if (o.status === "cancelled") return <Tag tone="neutral">Cancelada</Tag>
  const best = o.options.find((x) => x.ok && x.service_id === o.carrier_service_id) ?? null
  if (o.delivery_kind === "carrier" && best) {
    return (
      <div>
        <Tag tone="neutral">{best.carrier_name}</Tag>
        <div className="mt-1 font-mono text-[11.5px] text-muted-foreground">
          {eur(best.cost)} · chega {dayShort(best.arrival)}
        </div>
      </div>
    )
  }
  return (
    <div>
      <Tag tone={planned ? "warn" : "info"}>{planned ? "Por atribuir" : "Frota ou transportadora"}</Tag>
      <div className="mt-1 text-[11.5px] text-muted-foreground">{best ? `a bater: ${best.carrier_name} ${eur(best.cost)}` : "sem transportadora no prazo"}</div>
    </div>
  )
}

function AltCell({ o }: { o: Order }) {
  return (
    <div className="min-w-[22ch] text-[11.5px] text-muted-foreground">
      {o.options
        .filter((x) => !(o.delivery_kind === "carrier" && x.service_id === o.carrier_service_id))
        .map((x) => (
          <div key={x.service_id}>
            {x.carrier_name.replace("Transportadora ", "Transp. ")}: {x.ok ? eur(x.cost) : x.why}
          </div>
        ))}
    </div>
  )
}

function NoteCell({ o }: { o: Order }) {
  if (!o.delivery_note) return null
  const chips: string[] = []
  if (o.time_window_start) chips.push(`janela ${o.time_window_start}–${o.time_window_end}`)
  if (o.call_before_min) chips.push(`aviso prévio ${o.call_before_min} min`)
  for (const i of o.delivery_instructions ?? []) chips.push(i)
  return (
    <div className="mt-1 text-xs text-muted-foreground">
      “{o.delivery_note}”
      {chips.length > 0 && (
        <>
          {" → "}
          <span className="text-orange-700">{chips.join(" · ")}</span>
          {o.time_window_source === "ia" && !o.time_window_confirmed && (
            <span className="ml-1">
              <Tag tone="ai">por confirmar</Tag>
            </span>
          )}
        </>
      )}
    </div>
  )
}

export function Orders({ shell }: { shell: AppShell }) {
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [imports, setImports] = useState<OrderImport[]>([])
  const [error, setError] = useState<string | null>(null)
  const [params, setParams] = useSearchParams()
  const filter = (params.get("f") as Filter) || "all"
  const [q, setQ] = useState("")
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<ImportResult | StoreSyncResult | null>(null)
  const today = todayLisbon()
  const canEdit = shell.role >= 5
  // Atribuição à mão: plano atual (para a coluna «Entrega escolhida») e os diálogos.
  const [plan, setPlan] = useState<PlanView | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [bulk, setBulk] = useState<Order[] | null>(null)
  const [single, setSingle] = useState<Order | null>(null)
  const assignments = useMemo(() => assignmentsOf(plan, vehicles), [plan, vehicles])

  /** Garante o plano (sem proposta, um plano à mão vazio) antes de abrir um diálogo de atribuição. */
  const withPlan = async (open: () => void) => {
    try {
      const [p, v] = await Promise.all([plan?.plan ? Promise.resolve(plan) : ensureManualPlan(shell), vehicles.length ? Promise.resolve(vehicles) : listVehicles(shell)])
      setPlan(p)
      setVehicles(v)
      if (!v.some((x) => x.active !== false)) shell.toast("info", "Sem veículos ativos", "Só dá para atribuir a transportadoras. Acrescenta veículos em Administração.")
      open()
    } catch (err) {
      shell.toast("error", "Não foi possível abrir a atribuição", errorMessage(err))
    }
  }
  const reloadPlan = async () => {
    const [p] = await Promise.all([getPlan(shell), load()])
    setPlan(p)
    setSel(new Set())
  }

  const load = useCallback(async () => {
    try {
      const [o, i] = await Promise.all([listOrders(shell), listImports(shell)])
      getPlan(shell).then(setPlan).catch(() => undefined)
      listVehicles(shell).then(setVehicles).catch(() => undefined)
      setOrders(o)
      setImports(i)
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])

  async function runImport(file: File) {
    try {
      setProgress("A enviar o ficheiro…")
      const key = await uploadFile(shell, file)
      let r: ImportResult | null = null
      let offset: number | null = 0
      while (offset !== null) {
        r = await importOrdersBatch(shell, { file_key: key, file_name: file.name, import_id: r?.import_id, offset })
        setProgress(`A importar… faltam ${r.remaining}`)
        offset = r.next_offset
      }
      setLastRun(r)
      const s = r!.stats
      shell.toast("success", "Encomendas importadas", `${s.new} novas, ${s.changed} alteradas, ${s.cancelled} canceladas, ${s.unchanged} iguais`)
      await load()
    } catch (err) {
      shell.toast("error", "Importação falhou", errorMessage(err))
    } finally {
      setProgress(null)
    }
  }

  async function runStoreSync() {
    try {
      setProgress("A ler as encomendas da loja…")
      let r: StoreSyncResult | null = null
      let cursor: string | null | undefined = undefined
      let read = 0
      do {
        r = await syncStoreBatch(shell, { import_id: r?.import_id, cursor: cursor ?? undefined })
        read += r.read
        setProgress(`A importar da loja… ${read} lidas`)
        cursor = r.next_cursor
      } while (cursor)
      setLastRun(r)
      const s = r!.stats
      if (!read) shell.toast("info", "Nada novo na loja", r!.synced_since ? `Sem alterações desde ${new Date(r!.synced_since).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}.` : "O host ainda não tem encomendas sincronizadas.")
      else shell.toast("success", "Encomendas da loja importadas", `${s.new} novas, ${s.changed} alteradas, ${s.cancelled} canceladas, ${s.unchanged} iguais, ${s.ignored} noutro estado${s.withdrawn ? `, ${s.withdrawn} saíram das em aberto` : ""}`)
      await load()
    } catch (err) {
      shell.toast("error", "Importação da loja falhou", errorMessage(err))
    } finally {
      setProgress(null)
    }
  }

  const precision = useMemo(() => {
    const c = { cp7: 0, cp4: 0, address: 0, manual: 0, none: 0 }
    for (const o of orders ?? []) if (isOpen(o)) c[o.geo_precision]++
    return c
  }, [orders])

  // Distrito → concelho → localidade (da tabela de códigos postais), encadeados.
  const [geo, setGeo] = useState<{ district: string; municipality: string; locality: string }>({ district: "", municipality: "", locality: "" })
  const geoOptions = useMemo(() => {
    const all = orders ?? []
    const uniq = (list: Array<string | null | undefined>) => [...new Set(list.filter((v): v is string => Boolean(v)))].sort((a, b) => a.localeCompare(b, "pt"))
    const inDistrict = all.filter((o) => !geo.district || o.district === geo.district)
    const inMunicipality = inDistrict.filter((o) => !geo.municipality || o.municipality === geo.municipality)
    return { districts: uniq(all.map((o) => o.district)), municipalities: uniq(inDistrict.map((o) => o.municipality)), localities: uniq(inMunicipality.map((o) => o.locality)) }
  }, [orders, geo.district, geo.municipality])

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (orders ?? [])
      .filter((o) => match(o, filter, today))
      .filter((o) => (!geo.district || o.district === geo.district) && (!geo.municipality || o.municipality === geo.municipality) && (!geo.locality || o.locality === geo.locality))
      .filter((o) => !needle || [o.source_ref, o.customer_name, o.locality, o.municipality, o.postal_code].some((v) => v?.toLowerCase().includes(needle)))
  }, [orders, filter, q, today, geo])

  const changedAfterConfirm = (orders ?? []).filter((o) => o.changed_after_confirm)
  const last = imports[0]

  if (error) return <Page><ErrorBox error={error} /></Page>
  if (!orders) return <Loading />

  return (
    <Page>
      <Panel
        title="Importação"
        subtitle={
          last
            ? [last.file_key.startsWith("store:") ? "Loja" : last.file_name ?? "ficheiro", last.imported_at && !Number.isNaN(Date.parse(last.imported_at)) ? new Date(last.imported_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : null].filter(Boolean).join(" · ")
            : "Da loja ligada ao Koortex (Koonnect, Shopify) ou de um export (Excel ou CSV). Só entram as encomendas «pronta para expedir»."
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> Encomenda manual
            </Button>
            <AsyncButton
              variant="outline"
              size="sm"
              onRun={async () => {
                const total = await requoteAll(shell, false, (d, t) => setProgress(`A recalcular ${d}/${t}…`))
                setProgress(null)
                shell.toast("success", "Encomendas recalculadas", `${total} em aberto`)
                await load()
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Recalcular
            </AsyncButton>
            <AsyncButton size="sm" onRun={runStoreSync}>
              <Download className="h-3.5 w-3.5" /> Importar da loja
            </AsyncButton>
            <FileButton size="sm" variant="outline" accept=".xlsx,.xls,.csv" onFile={runImport}>
              Importar ficheiro
            </FileButton>
          </>
        }
      >
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
          {last && (
            <span>
              <b className="font-mono text-foreground">{last.stats.total_ready}</b> prontas: <b className="font-mono text-foreground">{last.stats.new}</b> novas,{" "}
              <b className="font-mono text-foreground">{last.stats.changed}</b> alteradas, <b className="font-mono text-foreground">{last.stats.cancelled}</b> canceladas,{" "}
              <b className="font-mono text-foreground">{last.stats.unchanged}</b> iguais · {last.stats.ignored} noutro estado{last.stats.withdrawn ? ` · ${last.stats.withdrawn} saíram das em aberto` : ""}
            </span>
          )}
          <span><b className="font-mono text-foreground">{precision.cp7}</b> CP7</span>
          <span><b className="font-mono text-foreground">{precision.cp4}</b> só CP4</span>
          <span><b className="font-mono text-foreground">{precision.address}</b> pela morada</span>
          <span><b className="font-mono text-foreground">{precision.manual}</b> pino manual</span>
          <span className={cn(precision.none && "text-red-700")}><b className="font-mono">{precision.none}</b> sem localização</span>
          {changedAfterConfirm.length > 0 && (
            <span className="text-amber-700">
              {changedAfterConfirm.length} alterada(s) depois de confirmada: {changedAfterConfirm.map((o) => o.source_ref).join(", ")} → decisão pendente
            </span>
          )}
        </div>
        {progress && <div className="mt-2 text-sm text-blue-700">{progress}</div>}
        {lastRun && (lastRun.warnings.length > 0 || lastRun.errors.length > 0) && (
          <div className="mt-2 space-y-0.5 text-xs text-amber-800">
            {[...lastRun.warnings, ...lastRun.errors].map((w, i) => <div key={i}>{w}</div>)}
          </div>
        )}
        <Note>
          Reimportar não duplica: cada encomenda é reconhecida pelo número. Se uma encomenda mudar na loja depois de confirmada, a app avisa e não mexe sozinha.
        </Note>
      </Panel>

      <Panel>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {FILTERS.map(([f, label]) => {
            const count = orders.filter((o) => match(o, f, today)).length
            return (
              <button
                key={f}
                onClick={() => setParams(f === "all" ? {} : { f })}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                  filter === f ? "border-accent bg-accent/10 text-foreground" : "text-muted-foreground hover:border-foreground/30",
                )}
              >
                {label}
                <span className="font-mono">{count}</span>
              </button>
            )
          })}
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <SearchSelect className="w-40 text-xs" ariaLabel="Distrito" allLabel="Distrito: todos" placeholder="Procurar distrito…" value={geo.district} onChange={(v) => setGeo({ district: v, municipality: "", locality: "" })} options={geoOptions.districts.map((d) => ({ value: d, label: d }))} />
            <SearchSelect className="w-40 text-xs" ariaLabel="Concelho" allLabel="Concelho: todos" placeholder="Procurar concelho…" value={geo.municipality} onChange={(v) => setGeo({ ...geo, municipality: v, locality: "" })} options={geoOptions.municipalities.map((d) => ({ value: d, label: d }))} />
            <SearchSelect className="w-40 text-xs" ariaLabel="Localidade" allLabel="Localidade: todas" placeholder="Procurar localidade…" value={geo.locality} onChange={(v) => setGeo({ ...geo, locality: v })} options={geoOptions.localities.map((d) => ({ value: d, label: d }))} />
            <input className={cn(inputCls, "w-56")} placeholder="Procurar nº, cliente, localidade…" value={q} onChange={(e) => setQ(e.target.value)} />
          </span>
        </div>
        {canEdit && sel.size > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-1.5 text-[12.5px]">
            <b>{sel.size} escolhida(s)</b>
            <Button size="sm" className="h-7" onClick={() => withPlan(() => setBulk((orders ?? []).filter((o) => sel.has(o.id))))}>
              <ArrowRightLeft className="h-3.5 w-3.5" /> Atribuir a veículo ou transportadora…
            </Button>
            <button className="text-muted-foreground underline" onClick={() => setSel(new Set())}>limpar</button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {canEdit && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Escolher todas as encomendas da lista"
                      checked={rows.length > 0 && rows.every((o) => sel.has(o.id))}
                      onChange={(e) => setSel(e.target.checked ? new Set([...sel, ...rows.filter(isOpen).map((o) => o.id)]) : new Set())}
                    />
                  </Th>
                )}
                <Th>Encomenda</Th>
                <Th>Cliente</Th>
                <Th>Morada</Th>
                <Th>Localização</Th>
                <Th>Carga</Th>
                <Th>Pronta</Th>
                <Th>Prazo</Th>
                <Th>Entrega escolhida</Th>
                <Th>Alternativas</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className={cn("cursor-pointer hover:bg-muted/50", sel.has(o.id) && "bg-accent/5")} onClick={() => setOpenId(o.id)}>
                  {canEdit && (
                    <td className="border-b px-2 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                      {isOpen(o) && (
                        <input
                          type="checkbox"
                          aria-label={`Escolher ${o.source_ref}`}
                          checked={sel.has(o.id)}
                          onChange={() => setSel((p) => { const next = new Set(p); if (next.has(o.id)) next.delete(o.id); else next.add(o.id); return next })}
                        />
                      )}
                    </td>
                  )}
                  <Td className="font-mono text-xs">
                    {o.source_ref}
                    {o.changed_after_confirm && <div><Tag tone="warn">mudou na origem</Tag></div>}
                    {o.manual && <div className="text-[11px] text-muted-foreground">manual</div>}
                  </Td>
                  <Td>{o.customer_name ?? "—"}</Td>
                  <Td>
                    {o.locality ?? "—"} <span className="font-mono text-[11.5px] text-muted-foreground">{o.postal_code}</span>
                    {(o.municipality || o.district) && <div className="text-[11.5px] text-muted-foreground">{[o.municipality, o.district].filter(Boolean).join(" · ")}</div>}
                    <NoteCell o={o} />
                  </Td>
                  <Td><GeoTag o={o} /></Td>
                  <Td>
                    <LoadCell o={o} />
                    <div className="text-[11.5px] text-muted-foreground">{o.parcels ? `${o.parcels} volume(s)` : ""}</div>
                  </Td>
                  <Td className="font-mono text-xs">{dm(day10(o.ready_at))}</Td>
                  <Td><DeadlineCell o={o} today={today} /></Td>
                  <Td><ChosenCell o={o} at={assignments.get(o.id)} planned={Boolean(plan?.plan) && isOpen(o) && o.status !== "blocked"} /></Td>
                  <Td><AltCell o={o} /></Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <Td colSpan={canEdit ? 10 : 9} className="py-8 text-center text-muted-foreground">
                    {orders.length === 0 ? "Ainda não há encomendas: importa o export da Koonnect." : "Nenhuma encomenda com este filtro."}
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {openId && (
        <OrderDialog
          shell={shell}
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          onAssign={canEdit ? (o) => withPlan(() => { setOpenId(null); setSingle(o) }) : undefined}
        />
      )}
      {single && plan?.plan && <MoveDialog shell={shell} order={single} view={plan} vehicles={vehicles} onClose={() => setSingle(null)} onApplied={reloadPlan} />}
      {bulk && plan?.plan && (
        <AssignDialog
          shell={shell}
          view={plan}
          vehicles={vehicles}
          candidates={bulk}
          preselected={bulk.map((o) => o.id)}
          onClose={() => setBulk(null)}
          onApplied={reloadPlan}
        />
      )}
      {creating && <NewOrderDialog shell={shell} onClose={() => setCreating(false)} onCreated={load} />}
    </Page>
  )
}

// ─── Detalhe ─────────────────────────────────────────────────────────────────

function OptionsTable({ options }: { options: CarrierOption[] }) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <Th>Transportadora</Th>
          <Th>Custo</Th>
          <Th>Recolha</Th>
          <Th>Chega</Th>
          <Th>Prazo</Th>
        </tr>
      </thead>
      <tbody>
        {options.map((x) => (
          <tr key={x.service_id}>
            <Td>
              {x.carrier_name} <span className="text-xs text-muted-foreground">· {x.service_name}</span>
            </Td>
            <Td className="font-mono text-xs">{eur(x.cost)}</Td>
            <Td className="font-mono text-xs">
              {dayShort(x.pickup)}
              {x.missed_cutoff && <div className="text-[11px] text-amber-700">hora limite já passou</div>}
            </Td>
            <Td className="font-mono text-xs">{dayShort(x.arrival)}</Td>
            <Td>{x.ok ? <Tag tone="ok">cumpre</Tag> : <span className="text-xs text-muted-foreground">{x.why}</span>}</Td>
          </tr>
        ))}
        {options.length === 0 && (
          <tr>
            <Td colSpan={5} className="text-muted-foreground">Sem transportadoras configuradas.</Td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

function OrderDialog({
  shell,
  id,
  onClose,
  onChanged,
  onAssign,
}: {
  shell: AppShell
  id: string
  onClose: () => void
  onChanged: () => Promise<void>
  /** «Atribuir…»: veículo × dia ou transportadora (gestor+). */
  onAssign?: (o: Order) => void
}) {
  const [order, setOrder] = useState<Order | null>(null)
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span className="font-mono">{order?.source_ref ?? "Encomenda"}</span>
            {order && onAssign && isOpen(order) && order.block_reason !== "geo" && (
              <Button size="sm" className="h-7" onClick={() => onAssign(order)}>
                <ArrowRightLeft className="h-3.5 w-3.5" /> Atribuir…
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <OrderDetail shell={shell} id={id} onChanged={onChanged} onLoaded={setOrder} />
      </DialogContent>
    </Dialog>
  )
}

/**
 * Detalhe de uma encomenda (morada, carga, artigos, transportadoras,
 * localização e janela horária). O mesmo no diálogo das Encomendas e no painel
 * do Despacho.
 */
export function OrderDetail({
  shell,
  id,
  onChanged,
  onLoaded,
  hideOptions,
  narrow,
}: {
  shell: AppShell
  id: string
  onChanged: () => Promise<void>
  onLoaded?: (o: Order) => void
  /** O Despacho mostra as opções com os botões de atribuir: não repete a tabela. */
  hideOptions?: boolean
  /** Painel estreito (Despacho): uma coluna. */
  narrow?: boolean
}) {
  const cols = narrow ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
  const [order, setOrder] = useState<(Order & { lines: OrderLine[] }) | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [lat, setLat] = useState("")
  const [lng, setLng] = useState("")
  const [win, setWin] = useState({ start: "", end: "" })
  const [cp, setCp] = useState("")
  const today = todayLisbon()

  const load = useCallback(async () => {
    try {
      const o = await getOrder(shell, id)
      setOrder(o)
      onLoaded?.(o)
      setLat(o.lat != null ? String(o.lat) : "")
      setLng(o.lng != null ? String(o.lng) : "")
      setWin({ start: o.time_window_start ?? "", end: o.time_window_end ?? "" })
      setCp(o.postal_code ?? "")
    } catch (e) {
      setErr(errorMessage(e))
    }
    // onLoaded é um callback do pai; só o id manda recarregar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, id])
  useEffect(() => void load(), [load])

  async function save(body: Record<string, unknown>, ok: string) {
    try {
      await patchOrder(shell, id, body)
      shell.toast("success", ok)
      await Promise.all([load(), onChanged()])
    } catch (e) {
      shell.toast("error", "Não foi possível gravar", errorMessage(e))
    }
  }

  const mapsQuery = order ? encodeURIComponent([order.address, order.postal_code, order.locality, "Portugal"].filter(Boolean).join(", ")) : ""

  if (err) return <ErrorBox error={err} />
  if (!order) return <Loading />
  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className={cn("grid gap-3", cols)}>
        <div>
          <div className="font-semibold">{order.customer_name ?? "—"}</div>
          <div className="text-muted-foreground">{order.address ?? "—"}</div>
          <div className="text-muted-foreground">
            {order.postal_code} {order.locality} {order.district ? `· ${order.district}` : ""}
          </div>
          {order.customer_phone && <div className="text-muted-foreground">{order.customer_phone}</div>}
        </div>
        <div className="space-y-1">
          <div>
            Pronta <b className="font-mono">{dm(day10(order.ready_at))}</b> · prazo <b className="font-mono">{dm(day10(order.due_date))}</b>
          </div>
          <DeadlineCell o={order} today={today} />
          <LoadCell o={order} />
          {order.status === "blocked" && <div className="text-red-700">Por resolver: {BLOCK_LABEL[order.block_reason ?? ""]}</div>}
          {order.changed_after_confirm && (
            <div className="flex items-center gap-2 text-amber-800">
              Mudou na Koonnect depois de confirmada.
              <Button size="sm" variant="outline" onClick={() => save({ changed_after_confirm: false }, "Aviso marcado como visto")}>Visto</Button>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-[13px] font-semibold">Artigos</h3>
        <table className="w-full border-collapse">
          <thead>
            <tr><Th>SKU</Th><Th>Descrição</Th><Th>Qtd</Th><Th>Peso/vol.</Th><Th>Classe</Th></tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id}>
                <Td className="font-mono text-xs">{l.sku}</Td>
                <Td>{l.description ?? "—"}</Td>
                <Td className="font-mono text-xs">{n(l.qty)}</Td>
                <Td className="font-mono text-xs">{l.unit_kg == null ? <Tag tone="bad">sem peso</Tag> : l.weight_source === "assumed" ? <span className="inline-flex items-center gap-1">{kg(l.unit_kg)} <Tag tone="warn">assumido</Tag></span> : kg(l.unit_kg)}</Td>
                <Td>{l.load_class ?? "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!hideOptions && (
        <div>
          <h3 className="mb-1 text-[13px] font-semibold">Transportadoras</h3>
          <OptionsTable options={order.options} />
        </div>
      )}

      <div className={cn("grid gap-4", cols)}>
        <div className="rounded-lg border p-3">
          <h3 className="mb-2 text-[13px] font-semibold">Localização · <GeoTag o={order} /></h3>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Código postal" className="col-span-2">
              <div className="flex gap-2">
                <input className={inputCls} value={cp} onChange={(e) => setCp(e.target.value)} />
                <Button size="sm" variant="outline" disabled={cp === (order.postal_code ?? "")} onClick={() => save({ postal_code: cp }, "Código postal corrigido")}>Corrigir</Button>
              </div>
            </Field>
            <Field label="Latitude"><input className={inputCls} value={lat} onChange={(e) => setLat(e.target.value)} /></Field>
            <Field label="Longitude"><input className={inputCls} value={lng} onChange={(e) => setLng(e.target.value)} /></Field>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => save({ lat, lng }, "Pino manual gravado")}>Gravar pino</Button>
            <a className="inline-flex items-center gap-1 text-xs text-accent underline" href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`} target="_blank" rel="noreferrer">
              Procurar no Google Maps <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <div className="rounded-lg border p-3">
          <h3 className="mb-2 text-[13px] font-semibold">
            Janela horária {order.time_window_source === "ia" && !order.time_window_confirmed && <Tag tone="ai">lida pela IA, por confirmar</Tag>}
            {order.time_window_source === "manual" && <Tag tone="neutral">à mão</Tag>}
          </h3>
          {order.delivery_note && <div className="mb-2 text-xs text-muted-foreground">Nota: “{order.delivery_note}”</div>}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Início"><input type="time" className={inputCls} value={win.start} onChange={(e) => setWin({ ...win, start: e.target.value })} /></Field>
            <Field label="Fim"><input type="time" className={inputCls} value={win.end} onChange={(e) => setWin({ ...win, end: e.target.value })} /></Field>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {order.time_window_source === "ia" && !order.time_window_confirmed && win.start === order.time_window_start && win.end === order.time_window_end ? (
              <Button size="sm" onClick={() => save({ time_window_confirmed: true }, "Janela confirmada")}>Confirmar</Button>
            ) : (
              <Button size="sm" variant="outline" disabled={!win.start || !win.end} onClick={() => save({ time_window_start: win.start, time_window_end: win.end }, "Janela gravada")}>Gravar</Button>
            )}
            {order.time_window_start && <Button size="sm" variant="ghost" onClick={() => save({ time_window_clear: true }, "Janela retirada")}>Tirar</Button>}
          </div>
          {(order.delivery_instructions?.length ?? 0) > 0 && (
            <ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground">
              {order.delivery_instructions!.map((i) => <li key={i}>{i}</li>)}
            </ul>
          )}
          {order.call_before_min && <div className="mt-1 text-xs text-muted-foreground">Ligar {order.call_before_min} min antes</div>}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
        {order.status === "cancelled" ? (
          <Button size="sm" variant="outline" onClick={() => save({ status: "ready" }, "Encomenda reaberta")}>Reabrir</Button>
        ) : (
          <Button size="sm" variant="outline" className="text-red-700" onClick={() => save({ status: "cancelled" }, "Encomenda cancelada")}>
            <Trash2 className="h-3.5 w-3.5" /> Cancelar encomenda
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Encomenda manual ────────────────────────────────────────────────────────

function NewOrderDialog({ shell, onClose, onCreated }: { shell: AppShell; onClose: () => void; onCreated: () => Promise<void> }) {
  const [f, setF] = useState<Record<string, string>>({ ready_at: todayLisbon() })
  const [lines, setLines] = useState([{ sku: "", description: "", qty: "1" }])
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Encomenda manual</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nº (vazio = automático)"><input className={inputCls} value={f.source_ref ?? ""} onChange={set("source_ref")} /></Field>
          <Field label="Pronta para expedir em"><input type="date" className={inputCls} value={f.ready_at ?? ""} onChange={set("ready_at")} /></Field>
          <Field label="Cliente"><input className={inputCls} value={f.customer_name ?? ""} onChange={set("customer_name")} /></Field>
          <Field label="Telefone"><input className={inputCls} value={f.customer_phone ?? ""} onChange={set("customer_phone")} /></Field>
          <Field label="Morada" className="col-span-2"><input className={inputCls} value={f.address ?? ""} onChange={set("address")} /></Field>
          <Field label="Código postal"><input className={inputCls} placeholder="2410-123" value={f.postal_code ?? ""} onChange={set("postal_code")} /></Field>
          <Field label="Localidade"><input className={inputCls} value={f.locality ?? ""} onChange={set("locality")} /></Field>
          <Field label="Nota de entrega" className="col-span-2" hint="A IA lê janela horária e instruções; fica por confirmar.">
            <input className={inputCls} value={f.delivery_note ?? ""} onChange={set("delivery_note")} />
          </Field>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-muted-foreground">Artigos</div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_70px_32px] gap-2">
              <input className={inputCls} placeholder="SKU" value={l.sku} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, sku: e.target.value } : x)))} />
              <input className={inputCls} placeholder="Descrição" value={l.description} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
              <input className={inputCls} type="number" min={1} value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="ghost" className="self-start" onClick={() => setLines([...lines, { sku: "", description: "", qty: "1" }])}>
            <Plus className="h-3.5 w-3.5" /> Artigo
          </Button>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <AsyncButton
            size="sm"
            onRun={async () => {
              try {
                await createOrder(shell, { ...f, lines: lines.map((l) => ({ ...l, qty: Number(l.qty) })) })
                shell.toast("success", "Encomenda criada")
                await onCreated()
                onClose()
              } catch (e) {
                shell.toast("error", "Não foi possível criar", errorMessage(e))
              }
            }}
          >
            Criar
          </AsyncButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}
